import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import {
  Ban,
  Check,
  Copy,
  KeyRound,
  LogOut,
  Pencil,
  RefreshCw,
  Save,
  ShieldCheck,
  Smartphone,
  Trash2,
  UserCheck,
  UserPlus,
  X,
} from 'lucide-react';
import { authApi, type AuthUser, type CreatedAuthUser } from './api';
import './AuthGate.css';

export function AuthGate({ children }: { children: (user: AuthUser) => ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    authApi.me().then(setUser).catch(() => {}).finally(() => setReady(true));
  }, []);

  async function login(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError('');
    setNotice('');
    setSubmitting(true);
    const data = new FormData(event.currentTarget);
    try {
      setUser(await authApi.login(String(data.get('phone')), String(data.get('password'))));
    } catch (value) {
      setError(value instanceof Error ? value.message : '登录失败');
    } finally {
      setSubmitting(false);
    }
  }

  async function changePassword(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError('');
    const data = new FormData(event.currentTarget);
    const currentPassword = String(data.get('currentPassword'));
    const newPassword = String(data.get('newPassword'));
    if (newPassword !== String(data.get('confirmPassword'))) {
      setError('两次输入的新密码不一致');
      return;
    }
    if (newPassword === currentPassword) {
      setError('新密码不能与临时密码相同');
      return;
    }
    setSubmitting(true);
    try {
      await authApi.changePassword(currentPassword, newPassword);
      setUser(null);
      setNotice('密码已修改，请使用新密码重新登录');
    } catch (value) {
      setError(value instanceof Error ? value.message : '密码修改失败');
    } finally {
      setSubmitting(false);
    }
  }

  if (!ready) return null;
  if (!user) {
    return (
      <div className="auth-page">
        <form className="auth-panel" onSubmit={login}>
          <div className="auth-mark"><ShieldCheck size={26} /></div>
          <h1>简泽图文工作台</h1>
          <p>使用手机号或管理员账号登录</p>
          <label>
            手机号或管理员账号
            <input name="phone" required autoComplete="username" inputMode="tel" autoFocus />
          </label>
          <label>
            密码
            <input name="password" type="password" required autoComplete="current-password" />
          </label>
          <button type="submit" disabled={submitting}>
            {submitting && <RefreshCw className="spinning" size={17} />}
            {submitting ? '正在登录' : '登录'}
          </button>
          {notice && <div className="auth-message success"><Check size={17} />{notice}</div>}
          {error && <div className="auth-message">{error}</div>}
          <small className="auth-help">新账号由管理员在“用户管理”中建立</small>
        </form>
      </div>
    );
  }
  if (user.mustChangePassword) {
    return (
      <div className="auth-page">
        <form className="auth-panel" onSubmit={changePassword}>
          <div className="auth-mark"><KeyRound size={25} /></div>
          <h1>设置你的新密码</h1>
          <p>首次登录需要更换临时密码</p>
          <label>当前临时密码<input name="currentPassword" type="password" required autoComplete="current-password" autoFocus /></label>
          <label>新密码<input name="newPassword" type="password" minLength={12} required autoComplete="new-password" /></label>
          <label>再次输入新密码<input name="confirmPassword" type="password" minLength={12} required autoComplete="new-password" /></label>
          <button type="submit" disabled={submitting}>{submitting ? '正在保存' : '保存新密码'}</button>
          {error && <div className="auth-message">{error}</div>}
          <small className="auth-help">至少 12 位，请勿与临时密码相同</small>
        </form>
      </div>
    );
  }
  return (
    <>
      {children(user)}
      <button className="logout-fab" title="退出登录" onClick={() => authApi.logout().then(() => setUser(null))}>
        <LogOut size={18} />
        <span>{user.displayName || user.username}</span>
      </button>
    </>
  );
}

const statusLabels: Record<AuthUser['status'], string> = {
  pending: '待审批',
  active: '正常',
  rejected: '已拒绝',
  disabled: '已禁用',
};

function formatLastLogin(value: number | null) {
  if (!value) return '尚未登录';
  return new Intl.DateTimeFormat('zh-CN', {
    month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false,
  }).format(new Date(value));
}

export function UserAdmin({ currentUser }: { currentUser: AuthUser }) {
  const [users, setUsers] = useState<AuthUser[]>([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [credential, setCredential] = useState<CreatedAuthUser | null>(null);
  const [copied, setCopied] = useState(false);
  const [editing, setEditing] = useState<{ id: number; displayName: string; phone: string } | null>(null);

  const load = () => authApi.users().then(setUsers).catch((value) => setError(value.message));
  useEffect(() => { void load(); }, []);

  async function createUser(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError('');
    setCredential(null);
    setBusy('create');
    const form = event.currentTarget;
    const data = new FormData(form);
    const password = String(data.get('password') ?? '').trim();
    try {
      const result = await authApi.createUser({
        displayName: String(data.get('displayName')),
        phone: String(data.get('phone')),
        ...(password ? { password } : {}),
      });
      setCredential(result);
      form.reset();
      await load();
    } catch (value) {
      setError(value instanceof Error ? value.message : '账号创建失败');
    } finally {
      setBusy(null);
    }
  }

  async function setStatus(id: number, status: AuthUser['status']) {
    if (status === 'disabled' && !window.confirm('禁用后，该账号当前登录会立即失效。确认禁用？')) return;
    setBusy(`status-${id}`);
    setError('');
    try {
      await authApi.setStatus(id, status);
      await load();
    } catch (value) {
      setError(value instanceof Error ? value.message : '状态修改失败');
    } finally {
      setBusy(null);
    }
  }

  async function updateAdminPhone(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy('admin-phone');
    setError('');
    const data = new FormData(event.currentTarget);
    try {
      await authApi.updateAdminPhone(String(data.get('phone')), String(data.get('currentPassword')));
      window.alert('管理员手机号已保存。为了保护账号安全，请使用手机号或 admin 重新登录。');
      window.location.reload();
    } catch (value) {
      setError(value instanceof Error ? value.message : '管理员手机号保存失败');
      setBusy(null);
    }
  }

  async function updateUser(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!editing) return;
    setBusy(`edit-${editing.id}`);
    setError('');
    const data = new FormData(event.currentTarget);
    try {
      await authApi.updateUser(editing.id, {
        displayName: String(data.get('displayName')),
        phone: String(data.get('phone')),
      });
      setEditing(null);
      await load();
    } catch (value) {
      setError(value instanceof Error ? value.message : '账号保存失败');
    } finally {
      setBusy(null);
    }
  }

  async function deleteUser(user: AuthUser) {
    if (!window.confirm(`确认永久删除 ${user.displayName || user.phone}？该账号会立即退出，且删除后无法恢复。`)) return;
    setBusy(`delete-${user.id}`);
    setError('');
    try {
      await authApi.deleteUser(user.id);
      if (editing?.id === user.id) setEditing(null);
      await load();
    } catch (value) {
      setError(value instanceof Error ? value.message : '账号删除失败');
    } finally {
      setBusy(null);
    }
  }

  async function resetPassword(user: AuthUser) {
    if (!window.confirm(`确认重置 ${user.displayName || user.phone} 的密码？该账号当前登录会立即失效。`)) return;
    setBusy(`reset-${user.id}`);
    setError('');
    try {
      setCredential(await authApi.resetPassword(user.id));
      await load();
    } catch (value) {
      setError(value instanceof Error ? value.message : '密码重置失败');
    } finally {
      setBusy(null);
    }
  }

  async function revokeSessions(user: AuthUser) {
    if (!window.confirm(`确认强制退出 ${user.displayName || user.phone} 的所有设备？`)) return;
    setBusy(`logout-${user.id}`);
    setError('');
    try {
      await authApi.revokeSessions(user.id);
    } catch (value) {
      setError(value instanceof Error ? value.message : '强制退出失败');
    } finally {
      setBusy(null);
    }
  }

  async function copyCredential() {
    if (!credential) return;
    try {
      await navigator.clipboard.writeText(`手机号：${credential.user.phone}\n临时密码：${credential.temporaryPassword}`);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      setError('复制失败，请手动记录临时登录信息');
    }
  }

  return (
    <section className="user-admin">
      <header><h1>用户管理</h1><p>建立运营子账号并控制工作台访问权限</p></header>

      <form className="admin-login-settings" onSubmit={updateAdminPhone}>
        <div className="admin-login-heading">
          <span className="admin-login-icon"><Smartphone size={20} /></span>
          <div><strong>管理员登录设置</strong><span>绑定后可用手机号或备用账号 admin 登录</span></div>
        </div>
        <label>备用账号<input value={currentUser.username} readOnly /></label>
        <label>管理员手机号<input name="phone" inputMode="tel" pattern="1[3-9][0-9]{9}" defaultValue={currentUser.phone} required placeholder="11 位手机号" /></label>
        <label>当前密码<input name="currentPassword" type="password" required autoComplete="current-password" placeholder="验证当前管理员密码" /></label>
        <button type="submit" disabled={Boolean(busy)}><Save size={17} />{busy === 'admin-phone' ? '正在保存' : currentUser.phone ? '更换手机号' : '绑定手机号'}</button>
      </form>

      <form className="user-create" onSubmit={createUser}>
        <div><strong>新增子账号</strong><span>留空初始密码时，系统会自动生成</span></div>
        <label>姓名<input name="displayName" maxLength={30} required placeholder="例如：小李" /></label>
        <label>手机号码<input name="phone" inputMode="tel" pattern="1[3-9][0-9]{9}" required placeholder="11 位手机号" /></label>
        <label>初始密码<input name="password" type="password" minLength={12} placeholder="留空自动生成" autoComplete="new-password" /></label>
        <button type="submit" disabled={busy === 'create'}><UserPlus size={17} />{busy === 'create' ? '正在建立' : '建立账号'}</button>
      </form>

      {credential && (
        <div className="credential-notice">
          <div><strong>临时登录信息</strong><span>{credential.user.displayName} · {credential.user.phone}</span></div>
          <code>{credential.temporaryPassword}</code>
          <button type="button" onClick={copyCredential}>{copied ? <Check size={17} /> : <Copy size={17} />}{copied ? '已复制' : '复制'}</button>
          <small>临时密码仅在这里显示一次，用户首次登录后必须修改。</small>
        </div>
      )}

      {error && <div className="auth-message">{error}</div>}
      <div className="user-table">
        {users.map((user) => (
          editing?.id === user.id ? (
            <form className="user-edit-row" key={user.id} onSubmit={updateUser}>
              <div><strong>编辑子账号</strong><span>保存后该账号需要重新登录</span></div>
              <label>姓名<input name="displayName" defaultValue={editing.displayName} maxLength={30} required autoFocus /></label>
              <label>手机号码<input name="phone" defaultValue={editing.phone} inputMode="tel" pattern="1[3-9][0-9]{9}" required /></label>
              <div className="user-edit-actions">
                <button type="submit" title="保存" disabled={Boolean(busy)}><Save size={17} /></button>
                <button type="button" title="取消" disabled={Boolean(busy)} onClick={() => setEditing(null)}><X size={17} /></button>
              </div>
            </form>
          ) : <div className="user-row" key={user.id}>
            <div className="user-identity">
              <strong>{user.displayName || user.username}</strong>
              <span>{user.role === 'admin' ? `${user.username}${user.phone ? ` · ${user.phone}` : ''}` : user.phone}</span>
            </div>
            <div className="user-login"><span>最近登录</span><strong>{formatLastLogin(user.lastLoginAt)}</strong></div>
            <span className={`status ${user.status}`}>{statusLabels[user.status]}</span>
            <div className="user-actions">
              {user.role !== 'admin' && (
                <>
                  <button title="编辑姓名和手机号" disabled={Boolean(busy)} onClick={() => setEditing({ id: user.id, displayName: user.displayName, phone: user.phone })}><Pencil size={17} /></button>
                  {user.status !== 'active' ? (
                    <button title="启用账号" disabled={Boolean(busy)} onClick={() => setStatus(user.id, 'active')}><UserCheck size={17} /></button>
                  ) : (
                    <button title="禁用账号" disabled={Boolean(busy)} onClick={() => setStatus(user.id, 'disabled')}><Ban size={17} /></button>
                  )}
                  <button title="重置密码" disabled={Boolean(busy)} onClick={() => resetPassword(user)}><KeyRound size={17} /></button>
                  <button title="强制退出所有设备" disabled={Boolean(busy)} onClick={() => revokeSessions(user)}><LogOut size={17} /></button>
                  <button className="danger" title="永久删除账号" disabled={Boolean(busy)} onClick={() => deleteUser(user)}><Trash2 size={17} /></button>
                </>
              )}
            </div>
          </div>
          )
        )}
      </div>
    </section>
  );
}
